import React, { useEffect, useRef, useState } from 'react';

import getFullDimensions from '../../../utils/getFullDimensions';
import { ContentHeading, ContentLink } from '../contentComponents';

import styles from './Toggler.module.css';

const Item = ({ item, index, isToggled, onClick }) => {
  const ref = useRef();
  const [height, setHeight] = useState();

  useEffect(() => {
    if (ref.current) {
      const button = ref.current.querySelector(
        '.toggle-item-heading-container',
      );
      const { height: baseHeight } = getFullDimensions(button);
      const content = ref.current.querySelector(
        '.toggle-item-content-container',
      );
      const { height: contentHeight } = getFullDimensions(content);

      if (isToggled) {
        setHeight(contentHeight + baseHeight);
        setTimeout(() => (content.style.opacity = 1), 200);
      } else if (!isToggled && height !== baseHeight) {
        setHeight(baseHeight);
        content.style.opacity = 0;
      }
    }
  }, [isToggled, ref]);

  return (
    <div
      className="toggle-item-container mb-8"
      style={{
        height: `${height}px`,
        transition: 'height .3s',
        overflow: 'hidden',
        maxWidth: '375px',
        width: '100%',
      }}
      ref={ref}
    >
      <button
        className={`toggle-item-heading-container pb-4 ${styles.header}`}
        onClick={onClick}
        data-index={index}
        aria-label={item.emoji}
      >
        <i
          className={`${styles.emoji} mr-4`}
          style={{ background: isToggled ? 'white' : 'white' }}
        >
          {item.emoji}
        </i>
        <ContentHeading
          text={item.heading}
          additionalStyles={{
            color: isToggled ? 'var(--blue)' : '',
            marginLeft: '12px',
            textAlign: 'left',
          }}
        />
      </button>
      <div
        className={`toggle-item-content-container ${styles.contentContainer}`}
        style={{
          transition: 'opacity .2s',
        }}
      >
        <p className="text-md font-medium mb-3 font-inter">{item.content}</p>
        {item.link && (
          <ContentLink
            text={item.link.text}
            href={item.link.href}
            asPath={item.link.asPath}
            isNotFocusable={!isToggled}
          />
        )}
      </div>
    </div>
  );
};

const TogglerFlex = ({
  toggles,
  currentPosition,
  setCurrentPosition,
  isAutomatic,
  time = 3250,
  setPreviousPosition,
}) => {
  const [highestHeight, setHighestHeight] = useState(null);
  const containerRef = useRef();

  useEffect(() => {
    // Effect to calculate a specific height so that the screen is not resizing
    if (containerRef.current) {
      // Calculate hightest height of toggler.
      const children = [
        ...containerRef.current.querySelectorAll('.toggle-item-container'),
      ];
      // This to calculate the base size
      const childHeadings = children.map((child) =>
        child.querySelector('.toggle-item-heading-container'),
      );
      const baseHeight = childHeadings.reduce((acc, elem) => {
        const { height } = getFullDimensions(elem);
        const { marginBottom, marginTop } = getFullDimensions(
          elem.closest('.toggle-item-container'),
        );
        return acc + height + marginBottom + marginTop;
      }, 0);
      let newHighestHeight = baseHeight;
      for (let i = 0; i < children.length; i++) {
        const candidate = getFullDimensions(children[i]).height + baseHeight;
        if (candidate > newHighestHeight) newHighestHeight = candidate;
      }
      setHighestHeight(newHighestHeight);
    }
  }, [containerRef]);

  useEffect(() => {
    let timeoutId;
    if (isAutomatic) {
      // Calculate new position
      let newPosition;
      if (currentPosition === toggles.length - 1) {
        newPosition = 0;
      } else newPosition = currentPosition + 1;
      // Set timeout
      timeoutId = setTimeout(() => {
        setCurrentPosition(newPosition);
      }, time);
    }

    return () => clearTimeout(timeoutId);
  }, [isAutomatic, toggles, currentPosition, time]);

  function handleClick(e) {
    setPreviousPosition(currentPosition);
    setCurrentPosition(parseInt(e.currentTarget.dataset.index, 10));
  }

  return (
    <div
      ref={containerRef}
      className={styles.container}
      style={{ height: highestHeight ? `${highestHeight}px` : 'initial' }}
    >
      {toggles.map((item, i) => (
        <Item
          key={i}
          item={item}
          isToggled={i === currentPosition}
          index={i}
          onClick={handleClick}
        />
      ))}
    </div>
  );
};

export default TogglerFlex;
